import React, { FC, SyntheticEvent, useCallback } from 'react';
import {
  Checkbox,
  type CheckboxProps,
  FormControlLabel,
  formControlLabelClasses,
  styled,
  type SxProps,
  type Theme
} from '@mui/material';

import CheckboxChecked from '@/assets/icons/checkbox-checked.svg?react';
import CheckboxEmpty from '@/assets/icons/checkbox-empty.svg?react';
import CheckboxIndeterminate from '@/assets/icons/checkbox-indeterminate.svg?react';
import withFormControl from '@/hoc/withFormControl';

interface CheckboxToggleProps {
  id?: string;
  name?: string;
  value?: boolean;
  disabled?: boolean;
  onChange?: (value: boolean) => void;
  sx?: SxProps<Theme>;
  label?: string;
  indeterminate?: boolean;
}

const CheckboxToggleField: FC<CheckboxToggleProps> = ({
  id,
  value,
  name,
  onChange,
  indeterminate,
  sx,
  label,
  disabled
}) => {
  const handleChange = useCallback(
    (_e: SyntheticEvent<Element>, value: boolean) => {
      onChange?.(value);
    },
    [onChange]
  );

  return (
    <StyledControlLabel
      disabled={disabled}
      onChange={handleChange}
      value={!!value}
      control={<StyledCheckbox checked={!!value} id={id} name={name} sx={sx} indeterminate={indeterminate} />}
      label={label}
    />
  );
};

const CheckboxToggle = withFormControl<boolean, boolean, CheckboxToggleProps>(CheckboxToggleField);

export default CheckboxToggle;

const StyledControlLabel = styled(FormControlLabel)(({ theme: { typography, palette }, value: checked }) => ({
  margin: 0,

  [`& .${formControlLabelClasses.label}`]: {
    ...typography.body3,
    fontWeight: typography.fontWeightMedium,
    color: checked ? palette.text.primary : palette.grey[700]
  },

  [`&.${formControlLabelClasses.disabled}`]: {
    color: palette.grey[500]
  }
}));

export const StyledCheckbox = styled((props: CheckboxProps) => (
  <Checkbox
    {...props}
    color="primary"
    icon={<CheckboxEmpty />}
    checkedIcon={<CheckboxChecked />}
    indeterminateIcon={<CheckboxIndeterminate />}
  />
))(({ theme: { spacing } }) => ({
  padding: 0,
  [`& svg`]: {
    width: spacing(4),
    height: spacing(4)
  }
}));
